import * as React from "react";
import { cn } from "@/utils/utils";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";

export interface SplitButtonProps extends React.HTMLAttributes<HTMLDivElement> {
  // Main button
  mainContent?: React.ReactNode;
  onMainClick?: () => void;
  mainButtonDisabled?: boolean;

  // Split button (arrow)
  splitContent?: React.ReactNode;
  onSplitClick?: () => void;
  isOpen?: boolean;
  splitButtonAsChild?: boolean;
  splitButtonDisabled?: boolean;

  // Styling options
  noBorder?: boolean;
  mainButtonClassName?: string;
  splitButtonClassName?: string;
}

const SplitButton = React.forwardRef<HTMLDivElement, SplitButtonProps>(
  (
    {
      className,
      mainContent,
      onMainClick,
      mainButtonDisabled,
      splitContent,
      onSplitClick,
      isOpen = false,
      splitButtonAsChild = false,
      splitButtonDisabled,
      noBorder = false,
      mainButtonClassName,
      splitButtonClassName,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn("flex items-center h-9", className)}
        {...props}
      >
        {mainContent && (
          <Button
            variant="secondary"
            className={cn(
              "h-full rounded-r-none",
              !noBorder && "border-r-0",
              noBorder && "border-r-0",
              mainButtonClassName
            )}
            onClick={onMainClick}
            disabled={mainButtonDisabled}
          >
            {mainContent}
          </Button>
        )}

        {(() => {
          const buttonContent =
            splitButtonAsChild && React.isValidElement(splitContent)
              ? splitContent.props.children
              : splitContent;

          const defaultContent = (
            <ChevronDown
              className={cn(
                "h-5 text-muted-foreground transition-transform duration-200",
                isOpen ? "rotate-180" : "rotate-0"
              )}
            />
          );

          const button = (
            <Button
              variant="secondary"
              selected={isOpen}
              className={cn(
                "h-full px-[2px]",
                mainContent
                  ? noBorder
                    ? "rounded-l-none border-l-0"
                    : "rounded-l-none border-l"
                  : "rounded-lg",
                splitButtonClassName
              )}
              onClick={onSplitClick}
              disabled={splitButtonDisabled}
            >
              {buttonContent || defaultContent}
            </Button>
          );

          return splitButtonAsChild && React.isValidElement(splitContent)
            ? React.cloneElement(
                splitContent as React.ReactElement<{
                  children?: React.ReactNode;
                }>,
                { children: button }
              )
            : button;
        })()}
      </div>
    );
  }
);

SplitButton.displayName = "SplitButton";

export { SplitButton };
